import gsap from "gsap";

export class Menu {
  constructor() {
    // GSAP matchMedia for responsive behavior
    this.mm = gsap.matchMedia();

    // Dropdown state management
    this.activeDropdown = false;
    this.dropdownsAnimating = false;

    // DOM elements
    this.headerBG = document.querySelector(".header__bg");
    this.menuItemsWithDropdowns = document.querySelectorAll(
      ".menu__item--with-children"
    );

    // Mobile menu properties
    this.menu = document.querySelector(".menu");
    this.menuToggle = document.querySelector(".menu-toggle");

    this.init();
  }

  /**
   * Initialize the menu component
   */
  init() {
    this.setupDropdowns();
    this.setupGlobalEventListeners();
    this.setupResponsiveMenu();
  }

  /**
   * Get animation duration based on user's motion preferences
   * @returns {number} Duration in seconds (0 for reduced motion)
   */
  getAnimationDuration() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : 0.25;
  }

  /**
   * Get mobile animation duration based on user's motion preferences
   * @returns {number} Duration in seconds (0 for reduced motion)
   */
  getMobileAnimationDuration() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : 0.25;
  }

  /**
   * Setup all dropdown menu items
   */
  setupDropdowns() {
    this.menuItemsWithDropdowns.forEach((menuItem) => {
      this.setupMenuItem(menuItem);
    });
  }

  /**
   * Setup individual menu item with dropdown functionality
   * @param {HTMLElement} menuItem - The menu item element
   */
  setupMenuItem(menuItem) {
    const menuLink = menuItem.querySelector(".menu__link");
    const arrow = menuItem.querySelector("svg");
    const dropdown = menuItem.querySelector(".dropdown");
    const dropdownItems = dropdown.querySelectorAll(".dropdown-item");
    const dropdownBackLink = dropdown.querySelector(".dropdown__back-link");

    this.setupResponsiveStyles(dropdown, dropdownItems, arrow, menuLink);
    this.attachDropdownMethods(dropdown, menuLink, arrow, dropdownItems);
    this.setupEventListeners(menuLink, dropdown, dropdownBackLink);
  }

  /**
   * Setup responsive styles for desktop and mobile breakpoints
   * @param {HTMLElement} dropdown - Dropdown element
   * @param {NodeList} dropdownItems - Dropdown item elements
   * @param {HTMLElement} arrow - Arrow element
   * @param {HTMLElement} menuLink - Menu link element
   */
  setupResponsiveStyles(dropdown, dropdownItems, arrow, menuLink) {
    // Desktop styles (1001px+)
    this.mm.add("(min-width: 1001px)", () => {
      gsap.set(dropdown, { xPercent: 0, yPercent: -100 });
      gsap.set(this.headerBG, { autoAlpha: 0 });
      gsap.set(dropdownItems, { autoAlpha: 0 });
      gsap.set(arrow, { rotate: 0 });

      menuLink.classList.remove("menu__link--open");
      menuLink.setAttribute("aria-expanded", false);
      this.activeDropdown = false;
    });

    // Mobile styles (1000px and below)
    this.mm.add("(max-width: 1000px)", () => {
      gsap.set(dropdown, { yPercent: 0, xPercent: 100 });
      gsap.set(this.headerBG, { autoAlpha: 0 });
      gsap.set(arrow, { rotate: -90 });

      menuLink.classList.remove("menu__link--open");
      menuLink.setAttribute("aria-expanded", false);
      this.activeDropdown = false;
    });
  }

  /**
   * Attach open/close methods to dropdown element for easy access
   * @param {HTMLElement} dropdown - Dropdown element
   * @param {HTMLElement} menuLink - Menu link element
   * @param {HTMLElement} arrow - Arrow element
   * @param {NodeList} dropdownItems - Dropdown item elements
   */
  attachDropdownMethods(dropdown, menuLink, arrow, dropdownItems) {
    dropdown.closeDropdown = () =>
      this.closeDropdown(dropdown, menuLink, arrow, dropdownItems);
    dropdown.openDropdown = () =>
      this.openDropdown(dropdown, menuLink, arrow, dropdownItems);
  }

  /**
   * Close dropdown with responsive animations
   * @param {HTMLElement} dropdown - Dropdown element to close
   * @param {HTMLElement} menuLink - Associated menu link
   * @param {HTMLElement} arrow - Arrow element
   * @param {NodeList} dropdownItems - Dropdown item elements
   */
  closeDropdown(dropdown, menuLink, arrow, dropdownItems) {
    if (this.dropdownsAnimating) return;

    const duration = this.getAnimationDuration();
    const mobileDuration = this.getMobileAnimationDuration();

    // Desktop close animation
    if (window.matchMedia("(min-width: 1001px)").matches) {
      gsap
        .timeline({
          defaults: { duration, ease: "power1.inOut" },
          onStart: () => {
            this.dropdownsAnimating = true;
          },
          onComplete: () => {
            this.dropdownsAnimating = false;
          },
        })
        .fromTo(dropdownItems, { autoAlpha: 1 }, { autoAlpha: 0 })
        .fromTo(dropdown, { yPercent: 0 }, { yPercent: -100 })
        .fromTo(arrow, { rotate: 180 }, { rotate: 0 }, "<")
        .fromTo(this.headerBG, { autoAlpha: 1 }, { autoAlpha: 0 }, "<")
        .set(dropdown, { autoAlpha: 0 });
    } else {
      // Mobile close animation
      gsap
        .timeline({
          defaults: { duration: mobileDuration, ease: "power1.inOut" },
          onStart: () => {
            this.dropdownsAnimating = true;
          },
          onComplete: () => {
            this.dropdownsAnimating = false;
          },
        })
        .fromTo(dropdown, { xPercent: 0 }, { xPercent: 100 })
        .set(dropdown, { autoAlpha: 0 });
    }

    menuLink.classList.remove("menu__link--open");
    menuLink.setAttribute("aria-expanded", false);
    this.activeDropdown = false;
  }

  /**
   * Open dropdown with responsive animations
   * Handles both first open and switching between dropdowns
   * @param {HTMLElement} dropdown - Dropdown element to open
   * @param {HTMLElement} menuLink - Associated menu link
   * @param {HTMLElement} arrow - Arrow element
   * @param {NodeList} dropdownItems - Dropdown item elements
   */
  openDropdown(dropdown, menuLink, arrow, dropdownItems) {
    if (this.dropdownsAnimating) return;

    const duration = this.getAnimationDuration();
    const mobileDuration = this.getMobileAnimationDuration();

    if (this.activeDropdown) {
      // Switch from one dropdown to another
      this.handleActiveDropdownSwitch(
        dropdown,
        menuLink,
        arrow,
        dropdownItems,
        duration,
        mobileDuration
      );
    } else {
      // Open first dropdown
      this.handleFirstDropdownOpen(
        dropdown,
        menuLink,
        arrow,
        dropdownItems,
        duration,
        mobileDuration
      );
    }

    menuLink.classList.add("menu__link--open");
    menuLink.setAttribute("aria-expanded", true);
    this.activeDropdown = dropdown;
  }

  /**
   * Handle switching from one active dropdown to another
   * @param {HTMLElement} dropdown - New dropdown to open
   * @param {HTMLElement} menuLink - New menu link
   * @param {HTMLElement} arrow - New arrow element
   * @param {NodeList} dropdownItems - New dropdown items
   * @param {number} duration - Animation duration for desktop
   * @param {number} mobileDuration - Animation duration for mobile
   */
  handleActiveDropdownSwitch(
    dropdown,
    menuLink,
    arrow,
    dropdownItems,
    duration,
    mobileDuration
  ) {
    // Get elements from currently active dropdown
    const activeMenuLink = this.activeDropdown
      .closest(".menu__item")
      .querySelector(".menu__link");
    const activeArrow = this.activeDropdown
      .closest(".menu__item")
      .querySelector("svg");
    const activeDropdownItems =
      this.activeDropdown.querySelectorAll(".dropdown-item");

    // Update link states
    activeMenuLink.classList.remove("menu__link--open");
    menuLink.classList.add("menu__link--open");

    // Desktop dropdown switch animation
    if (window.matchMedia("(min-width: 1001px)").matches) {
      gsap
        .timeline({
          defaults: { duration, ease: "power1.inOut" },
          onStart: () => {
            this.dropdownsAnimating = true;
          },
          onComplete: () => {
            this.dropdownsAnimating = false;
          },
        })
        .to(activeArrow, { rotate: 0 })
        .to(arrow, { rotate: 180 }, "<")
        .to(activeDropdownItems, { autoAlpha: 0 }, "<")
        .to(this.activeDropdown, { height: () => dropdown.clientHeight })
        .set(this.activeDropdown, { autoAlpha: 0, yPercent: -100 })
        .set(dropdown, { yPercent: 0, autoAlpha: 1 })
        .to(dropdownItems, { stagger: 0.025, autoAlpha: 1 })
        .set(this.activeDropdown, { clearProps: "height, autoAlpha" });
    } else {
      // Mobile dropdown switch animation
      gsap
        .timeline({
          defaults: { duration: mobileDuration, ease: "power1.inOut" },
          onStart: () => {
            this.dropdownsAnimating = true;
          },
          onComplete: () => {
            this.dropdownsAnimating = false;
          },
        })
        .set(this.activeDropdown, { autoAlpha: 0, xPercent: 100 })
        .set(dropdown, { xPercent: 0, autoAlpha: 1 })
        .set(this.activeDropdown, { clearProps: "autoAlpha" });
    }
  }

  /**
   * Handle opening the first dropdown when none are active
   * @param {HTMLElement} dropdown - Dropdown to open
   * @param {HTMLElement} menuLink - Menu link element
   * @param {HTMLElement} arrow - Arrow element
   * @param {NodeList} dropdownItems - Dropdown items
   * @param {number} duration - Animation duration for desktop
   * @param {number} mobileDuration - Animation duration for mobile
   */
  handleFirstDropdownOpen(
    dropdown,
    menuLink,
    arrow,
    dropdownItems,
    duration,
    mobileDuration
  ) {
    // Desktop first open animation
    if (window.matchMedia("(min-width: 1001px)").matches) {
      gsap
        .timeline({
          defaults: { duration, ease: "power1.inOut" },
          onStart: () => {
            this.dropdownsAnimating = true;
          },
          onComplete: () => {
            this.dropdownsAnimating = false;
          },
        })
        .set(dropdown, { autoAlpha: 1 }, 0)
        .fromTo(arrow, { rotate: 0 }, { rotate: 180 }, 0)
        .fromTo(dropdown, { yPercent: -100 }, { yPercent: 0 }, 0)
        .fromTo(this.headerBG, { autoAlpha: 0 }, { autoAlpha: 1 }, "<")
        .fromTo(
          dropdownItems,
          { autoAlpha: 0 },
          { stagger: 0.05, autoAlpha: 1 }
        );
    } else {
      // Mobile first open animation
      gsap
        .timeline({
          defaults: { duration: mobileDuration, ease: "power1.inOut" },
          onStart: () => {
            this.dropdownsAnimating = true;
          },
          onComplete: () => {
            this.dropdownsAnimating = false;
          },
        })
        .set(dropdown, { autoAlpha: 1 }, 0)
        .fromTo(dropdown, { xPercent: 100 }, { xPercent: 0 }, 0);
    }
  }

  /**
   * Setup event listeners for menu interactions
   * @param {HTMLElement} menuLink - Menu link element
   * @param {HTMLElement} dropdown - Dropdown element
   * @param {HTMLElement} dropdownBackLink - Back link in dropdown
   */
  setupEventListeners(menuLink, dropdown, dropdownBackLink) {
    // Toggle dropdown on menu link click
    menuLink.addEventListener("click", (e) => {
      menuLink.classList.contains("menu__link--open")
        ? dropdown.closeDropdown()
        : dropdown.openDropdown();
      e.preventDefault();
    });

    // Close dropdown on back link click (mobile)
    dropdownBackLink.addEventListener("click", () => {
      dropdown.closeDropdown();
    });
  }

  /**
   * Setup global event listeners for menu interactions
   */
  setupGlobalEventListeners() {
    // Close dropdown when clicking header background overlay
    this.headerBG.addEventListener("click", () => {
      if (this.activeDropdown) {
        this.activeDropdown.closeDropdown();
      }
    });

    // Close dropdown on Escape key press
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.activeDropdown) {
        this.activeDropdown.closeDropdown();
      }
    });
  }

  /**
   * Setup responsive menu functionality
   * Handles mobile menu toggle
   */
  setupResponsiveMenu() {
    this.setupMobileMenuElements();
  }

  /**
   * Setup mobile menu elements and event listeners
   */
  setupMobileMenuElements() {
    if (!this.menu || !this.menuToggle) return;

    // Set initial ARIA states
    this.menuToggle.setAttribute("aria-expanded", "false");
    this.menu.setAttribute("aria-expanded", "false");

    // Bind and attach toggle event listener
    this.menuToggleEvent = this.handleMenuToggle.bind(this);
    this.menuToggle.addEventListener("click", this.menuToggleEvent);
  }

  /**
   * Handle mobile menu toggle button click
   * Closes any open dropdowns first, then toggles mobile menu
   */
  handleMenuToggle() {
    // Close any open dropdown first
    if (this.activeDropdown) {
      this.activeDropdown.closeDropdown();
      return;
    }

    const duration = this.getMobileAnimationDuration();

    // Toggle mobile menu based on current state
    if (this.menuToggle.getAttribute("aria-expanded") === "true") {
      this.closeMobileMenu(duration);
    } else {
      this.openMobileMenu(duration);
    }
  }

  /**
   * Close mobile menu with animation
   * @param {number} duration - Animation duration
   */
  closeMobileMenu(duration) {
    // Update ARIA states
    this.menuToggle.setAttribute("aria-expanded", "false");
    this.menu.setAttribute("aria-expanded", "false");

    // Animate menu closed
    gsap
      .timeline({
        defaults: { duration, ease: "power1.inOut" },
      })
      .fromTo(this.menu, { yPercent: 0 }, { yPercent: -100 })
      .set(this.menu, { autoAlpha: 0 })
      .set(this.menu, { clearProps: "transform" });
  }

  /**
   * Open mobile menu with animation
   * @param {number} duration - Animation duration
   */
  openMobileMenu(duration) {
    // Update ARIA states
    this.menuToggle.setAttribute("aria-expanded", "true");
    this.menu.setAttribute("aria-expanded", "true");

    // Animate menu open
    gsap
      .timeline({
        defaults: { duration, ease: "power1.inOut" },
      })
      .set(this.menu, { autoAlpha: 1 })
      .fromTo(this.menu, { yPercent: -100 }, { yPercent: 0 }, "<")
      .set(this.menu, { clearProps: "transform" });
  }
}
